import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {environment} from '../../../../../../../environments/environment';
import {InfoPanelType} from '../../../../../common/components';
import {ApplicationUtil} from '../../../../../obd/obd-common/utils/ApplicationUtil';
import {ApplicationStatusLookupDTO} from '../../../../models/application/ApplicationStatusLookupDTO';
import {OnboardingAdminPortalService} from '../../../../services/onboard-application.service';
import {ApplicationDTO} from '../../../../models/application/ApplicationDTO';
import {take} from 'rxjs/operators';
import {LookupValue} from '../../../../../clm/types/lookup-value.enum';
import {NameValueCodeDTO} from '../../../../../common/models/NameValueCodeDTO';
import {LookupService} from '../../../../../common/services/lookup.service';
import {SharedService} from '../../../../../common/services/shared-service';
import {EnterpriseRoutesQueryParam} from '../../../../../common/types/routes-query-param.enum';
import {PortalType} from '../../../../../common/types/portal.enum';
import {RuleMessageDialogData} from "../../../../models/application/ApplicationRuleMessageDTO";
import {RuleMessageDialogComponent} from "../../../dialogs/rule-message-dialog/rule-message-dialog.component";
import {MatDialog} from "@angular/material/dialog";

export enum ApplicationTab {
  Summary = 'summary',
  BankDetails = 'bankDetails',
  Addresses = 'addresses',
  Documents = 'documents',
  FamilyProviders = 'familyProviders',
  SMSes = 'smses',
  Emails = 'emails'
}

@Component({
  selector: 'greydom-review-application',
  templateUrl: './review-application.component.html',
  styleUrls: ['./review-application.component.scss']
})
export class ReviewApplicationComponent implements OnInit {
  readonly lookupValue = LookupValue;
  readonly PortalType = PortalType;
  readonly ApplicationTab = ApplicationTab;
  private readonly allNavItems = [
    {name: 'Summary', icon: 'summarize', value: ApplicationTab.Summary},
    {name: 'Bank Details', icon: 'account_balance', value: ApplicationTab.BankDetails},
    {name: 'Addresses', icon: 'location_on', value: ApplicationTab.Addresses},
    {name: 'Documents', icon: 'description', value: ApplicationTab.Documents},
    {name: 'Family Providers', icon: 'family_restroom', value: ApplicationTab.FamilyProviders},
    {name: 'SMSes', icon: 'sms', value: ApplicationTab.SMSes},
    {name: 'Emails', icon: 'email', value: ApplicationTab.Emails}
  ];

  private readonly baseNavValues = [ApplicationTab.Summary, ApplicationTab.BankDetails, ApplicationTab.Addresses, ApplicationTab.Documents, ApplicationTab.FamilyProviders];
  private readonly extendedNavValues = [...this.baseNavValues, ApplicationTab.SMSes, ApplicationTab.Emails];

  private readonly portalNavValues = {
    [PortalType.Broker]: this.baseNavValues,
    [PortalType.OnBoarding]: this.extendedNavValues,
    [PortalType.Normal]: this.extendedNavValues
  };
  selectedTab: ApplicationTab = ApplicationTab.Summary;
  application: ApplicationDTO;
  externalStatus: ApplicationStatusLookupDTO = null;
  tabName: string;
  backPageName: string;
  customRoute: string;
  currentPortal: PortalType;
  diagnosisGroups: NameValueCodeDTO[];
  heading: string;
  userFirstLastName = '';

  get visibleNavItems() {
    const allowedValues = this.portalNavValues[this.currentPortal] || [];
    return this.allNavItems.filter(item => allowedValues.includes(item.value));
  }

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private applicationService: OnboardingAdminPortalService,
    private lookupService: LookupService,
    private sharedService: SharedService,
    private applicationOnboardingService: OnboardingAdminPortalService,
  ) {
    const parentData = this.route.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || PortalType.Normal;
    this.customRoute = this.getCustomRoute();
    this.tabName = EnterpriseRoutesQueryParam.Applications;
    this.backPageName = 'enterprise-management';
    this.diagnosisGroups = [];
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      const applicationId = params['id'];
      this.getApplicationBy(applicationId);
    });
    this.userFirstLastName = this.sharedService.getLoggedInUserName();
    this.getListOfLookupValues(this.lookupValue.DiagnosisGroup);
  }

  getApplicationBy(applicationId: number) {
    this.applicationService.getApplicationById(applicationId)
      .pipe(take(1))
      .subscribe({
        next: application => {
          this.application = application;
          this.heading = `Review Application - ${application.membership?.principalLastName ?? ''}`;

          if (this.canStatusBeRetrievedFromExternalSystem()) {
            this.applicationOnboardingService.getApplicationStatusV2(this.application.membership.membershipNumber,
              this.application.intermediatory.intermediatoryCode).subscribe({
              next: (response) => this.externalStatus = response
            });
          } else {
            ApplicationUtil.extractStatusFromApplication(this.application).subscribe({
              next: (response) => this.externalStatus = response
            });
          }
        },
        error: error => {
          console.error('Error fetching application:', error);
        }
      });
  }

  private canStatusBeRetrievedFromExternalSystem() {
    return this.application.membership.membershipNumber != null && this.application.intermediatory.intermediatoryCode != null && this.application.stage.applicationStageId > 7;
  }

  handleReFetchApplication($event: boolean): void {
    if ($event) {
      this.getApplicationBy(this.application.applicationId);
    }
  }

  getApplicationStatus(): string {
    if (this.externalStatus != null) {
      return this.externalStatus.response.status;
    }
    return this.application?.status?.statusName;
  }

  getApplicationStatusMessage(): string {
    if (this.externalStatus != null) {
      return this.externalStatus.response.statusDescription;
    }
  }

  getMessageCountForStatus(): number {
    if (this.externalStatus != null) {
      return this.externalStatus.response.messages.length;
    }

    return 0;
  }

  onClickOpenReport($event: MouseEvent, applicationId: number): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!applicationId) {
      return;
    }
    const dialogData: RuleMessageDialogData = {
      application: this.application,
      status: this.application.status?.statusName || '',
      stage: this.application.stage?.stageName || '',
      showDownloadButton: false,
    };

    this.dialog.open(RuleMessageDialogComponent, {
      width: '80vw',
      height: '80vh',
      data: dialogData,
    });
  }

  private getCustomRoute(): string {
    const routes = {
      [PortalType.Normal]: null,
      [PortalType.OnBoarding]: '/onboarding/applications',
      [PortalType.Broker]: '/broker/applications',
    };
    return routes[this.currentPortal] || null;
  }

  private getListOfLookupValues(entity: LookupValue.DiagnosisGroup | LookupValue) {
    this.lookupService.getListOfLookupValuesWithCode(entity).subscribe({
      next: (response: NameValueCodeDTO[]) => {
        if (entity === this.lookupValue.DiagnosisGroup) {
          this.diagnosisGroups = response;
        }
      },
      error: (errorResponse: any) => {
        const error = errorResponse.error;
        this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
      }
    });
  }
}
